/**
 * 10.5K — PELES PROCEDURAIS das criaturas (canvas, sem assets). Cada pele é uma textura em
 * tons de cinza usada como `map` (multiplica a cor da criatura) E como `bumpMap` (relevo
 * barato): placas de quitina, escamas, rocha rachada, couro e facetas de cristal.
 * Texturas ficam em cache por tipo (perf mobile) — materiais continuam por instância.
 */
import * as THREE from "three";

export type SkinKind = "chitin" | "scales" | "stone" | "hide" | "crystal";

const cache = new Map<string, THREE.CanvasTexture>();

// gerador determinístico (a mesma pele em todo device)
function rng(seed: number): () => number {
  let s = seed >>> 0;
  return () => {
    s = (s * 1664525 + 1013904223) >>> 0;
    return s / 4294967296;
  };
}

function base(ctx: CanvasRenderingContext2D, S: number, tone: number): void {
  ctx.fillStyle = `rgb(${tone},${tone},${tone})`;
  ctx.fillRect(0, 0, S, S);
}
function noise(ctx: CanvasRenderingContext2D, S: number, rand: () => number, count: number, amp: number, size: number): void {
  for (let i = 0; i < count; i++) {
    const v = Math.round((rand() - 0.5) * amp);
    ctx.fillStyle = `rgba(${128 + v},${128 + v},${128 + v},0.5)`;
    const r = size * (0.5 + rand());
    ctx.beginPath();
    ctx.arc(rand() * S, rand() * S, r, 0, Math.PI * 2);
    ctx.fill();
  }
}

function drawChitin(ctx: CanvasRenderingContext2D, S: number, rand: () => number): void {
  base(ctx, S, 205);
  noise(ctx, S, rand, 180, 60, 5);
  // placas sobrepostas em fileiras (bordas escuras + brilho na crista)
  const rows = 6;
  const ph = S / rows;
  for (let r = 0; r <= rows; r++) {
    const cols = 4;
    const pw = S / cols;
    for (let c = -1; c <= cols; c++) {
      const x = c * pw + (r % 2 ? pw / 2 : 0);
      const y = r * ph;
      ctx.fillStyle = "rgba(60,60,60,0.55)";
      ctx.beginPath();
      ctx.roundRect(x + 2, y - ph * 0.5, pw - 4, ph * 1.05, 10);
      ctx.fill();
      ctx.fillStyle = "rgba(235,235,235,0.9)";
      ctx.beginPath();
      ctx.roundRect(x + 5, y - ph * 0.5 + 3, pw - 10, ph * 0.86, 9);
      ctx.fill();
      ctx.fillStyle = "rgba(255,255,255,0.35)";
      ctx.fillRect(x + 8, y - ph * 0.5 + 5, pw - 16, 3);
    }
  }
}

function drawScales(ctx: CanvasRenderingContext2D, S: number, rand: () => number): void {
  base(ctx, S, 200);
  noise(ctx, S, rand, 140, 50, 4);
  const sw = S / 8;
  const sh = sw * 0.8;
  for (let r = -1; r <= S / sh + 1; r++) {
    for (let c = -1; c <= 9; c++) {
      const x = c * sw + (r % 2 ? sw / 2 : 0);
      const y = r * sh;
      ctx.fillStyle = "rgba(55,55,55,0.6)";
      ctx.beginPath();
      ctx.arc(x, y, sw * 0.56, 0, Math.PI, false);
      ctx.fill();
      ctx.fillStyle = "rgba(225,225,225,0.95)";
      ctx.beginPath();
      ctx.arc(x, y - 2, sw * 0.5, 0, Math.PI, false);
      ctx.fill();
      ctx.fillStyle = "rgba(255,255,255,0.3)";
      ctx.beginPath();
      ctx.arc(x, y - 3, sw * 0.3, Math.PI * 1.1, Math.PI * 1.9, false);
      ctx.fill();
    }
  }
}

function drawStone(ctx: CanvasRenderingContext2D, S: number, rand: () => number): void {
  base(ctx, S, 190);
  noise(ctx, S, rand, 320, 90, 6);
  // rachaduras: polilinhas escuras com borda clara
  for (let i = 0; i < 14; i++) {
    let x = rand() * S;
    let y = rand() * S;
    ctx.beginPath();
    ctx.moveTo(x, y);
    const segs = 4 + Math.floor(rand() * 5);
    for (let s = 0; s < segs; s++) {
      x += (rand() - 0.5) * 40;
      y += (rand() - 0.5) * 40;
      ctx.lineTo(x, y);
    }
    ctx.lineWidth = 4;
    ctx.strokeStyle = "rgba(240,240,240,0.45)";
    ctx.stroke();
    ctx.lineWidth = 2;
    ctx.strokeStyle = "rgba(40,40,40,0.9)";
    ctx.stroke();
  }
}

function drawHide(ctx: CanvasRenderingContext2D, S: number, rand: () => number): void {
  base(ctx, S, 200);
  noise(ctx, S, rand, 900, 70, 2.2);
  // listras suaves diagonais (leitura de predador)
  for (let i = 0; i < 9; i++) {
    const x = i * (S / 8) + rand() * 10;
    ctx.strokeStyle = `rgba(70,70,70,${0.18 + rand() * 0.2})`;
    ctx.lineWidth = 6 + rand() * 8;
    ctx.beginPath();
    ctx.moveTo(x, -10);
    ctx.bezierCurveTo(x + 20, S * 0.3, x - 20, S * 0.7, x + 10, S + 10);
    ctx.stroke();
  }
}

function drawCrystal(ctx: CanvasRenderingContext2D, S: number, rand: () => number): void {
  base(ctx, S, 210);
  // facetas: triângulos claros/escuros
  const n = 7;
  const cell = S / n;
  for (let r = 0; r < n; r++) {
    for (let c = 0; c < n; c++) {
      const x = c * cell;
      const y = r * cell;
      const t1 = 170 + Math.round(rand() * 70);
      const t2 = 150 + Math.round(rand() * 70);
      ctx.fillStyle = `rgb(${t1},${t1},${t1})`;
      ctx.beginPath();
      ctx.moveTo(x, y); ctx.lineTo(x + cell, y); ctx.lineTo(x, y + cell); ctx.closePath();
      ctx.fill();
      ctx.fillStyle = `rgb(${t2},${t2},${t2})`;
      ctx.beginPath();
      ctx.moveTo(x + cell, y); ctx.lineTo(x + cell, y + cell); ctx.lineTo(x, y + cell); ctx.closePath();
      ctx.fill();
    }
  }
  ctx.strokeStyle = "rgba(255,255,255,0.35)";
  ctx.lineWidth = 1.5;
  for (let i = 0; i <= n; i++) {
    ctx.beginPath(); ctx.moveTo(i * cell, 0); ctx.lineTo(i * cell, S); ctx.stroke();
    ctx.beginPath(); ctx.moveTo(0, i * cell); ctx.lineTo(S, i * cell); ctx.stroke();
  }
}

const DRAW: Record<SkinKind, (ctx: CanvasRenderingContext2D, S: number, rand: () => number) => void> = {
  chitin: drawChitin,
  scales: drawScales,
  stone: drawStone,
  hide: drawHide,
  crystal: drawCrystal,
};

/** Textura da pele (cache por tipo × densidade). Usar como `map` + `bumpMap`.
 * `density` = quantas vezes o padrão repete (criaturas GRANDES usam mais, senão vira "casco de tartaruga"). */
export function skinTexture(kind: SkinKind, density = 2): THREE.CanvasTexture {
  const key = `${kind}:${density}`;
  let t = cache.get(key);
  if (t) return t;
  const S = 128;
  const c = document.createElement("canvas");
  c.width = S;
  c.height = S;
  const ctx = c.getContext("2d")!;
  DRAW[kind](ctx, S, rng(kind.length * 7919 + 17));
  t = new THREE.CanvasTexture(c);
  t.wrapS = t.wrapT = THREE.RepeatWrapping;
  t.repeat.set(density, density);
  t.colorSpace = THREE.SRGBColorSpace;
  t.userData.shared = true;
  cache.set(key, t);
  return t;
}

/** Aplica a pele em um material padrão (map + relevo). Sem alocação de textura extra. */
export function applySkin(mat: THREE.MeshStandardMaterial, kind: SkinKind, bumpScale = 0.035, density = 2): THREE.MeshStandardMaterial {
  const t = skinTexture(kind, density);
  mat.map = t;
  mat.bumpMap = t;
  mat.bumpScale = bumpScale;
  mat.needsUpdate = true;
  return mat;
}
